import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Heart, ArrowDown, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { weddingData, images } from '../mockData';
import WeddingCountdown from '../components/Countdown';
import AddToCalendar from '../components/AddToCalendar';
import KeyMoments from '../components/KeyMoments';

const Home = () => {
  const { language } = useLanguage();
  const content = weddingData[language].hero;
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* ═══════════════════════════════════════════════════════════════
          HERO SECTION - Photo mise en valeur
          ═══════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image - Haute qualité */}
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Nelly & Cyrille"
            className="w-full h-full object-cover object-center"
            style={{ 
              transform: `translateY(${scrollY * 0.2}px) scale(1.05)`,
              filter: 'brightness(1.02) contrast(1.02)'
            }}
          />
          {/* Overlay blanc doux pour lisibilité */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/50 to-white/80" />
        </div>

        {/* Decorative elements subtils */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -left-20 w-64 sm:w-80 h-64 sm:h-80 bg-lilac-200/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-64 sm:w-80 h-64 sm:h-80 bg-gold-200/20 rounded-full blur-3xl" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32 text-center">
          
          {/* Decorative Heart */}
          <div className={`mb-6 sm:mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}>
            <Heart className="mx-auto text-gold-500 fill-gold-500 drop-shadow-lg w-10 h-10 sm:w-12 sm:h-12" />
          </div>

          {/* Names */}
          <h1 
            className={`font-playfair font-extrabold text-plum-900 mb-6 sm:mb-8 transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ 
              fontSize: 'clamp(2.5rem, 12vw, 6rem)',
              lineHeight: '1',
              textShadow: '0 2px 10px rgba(255,255,255,0.8)'
            }}
          >
            Nelly <span className="text-gold-500 inline-block mx-1 sm:mx-3">&</span> Cyrille
          </h1>

          {/* Date */}
          <div className={`flex flex-wrap items-center justify-center gap-2 sm:gap-4 mb-8 sm:mb-10 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="hidden sm:block w-12 md:w-20 h-px bg-gradient-to-r from-transparent to-gold-400" />
            <div className="flex items-center gap-2 sm:gap-3 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full">
              <Calendar className="text-gold-500 w-5 h-5 sm:w-6 sm:h-6" />
              <p className="text-lg sm:text-xl md:text-2xl font-montserrat font-semibold text-plum-900 tracking-wide">
                {content.date}
              </p>
            </div>
            <div className="hidden sm:block w-12 md:w-20 h-px bg-gradient-to-l from-transparent to-gold-400" />
          </div>

          {/* Subtitle */}
          <p className={`text-base sm:text-lg md:text-xl font-cormorant italic text-plum-700 max-w-2xl mx-auto mb-8 sm:mb-12 px-4 leading-relaxed transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {content.subtitle}
          </p>

          {/* Countdown */}
          <div className={`mb-8 sm:mb-12 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <WeddingCountdown />
          </div>

          {/* Bible Verse Card - Sur fond blanc */}
          <div className={`bg-white rounded-2xl p-6 sm:p-8 md:p-10 mb-8 sm:mb-12 max-w-2xl mx-auto shadow-medium border border-lilac-100 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <Heart className="mx-auto text-gold-500 fill-gold-500 w-7 h-7 sm:w-8 sm:h-8 mb-4" />
            <p className="text-plum-800 font-montserrat text-sm sm:text-base md:text-lg leading-relaxed mb-4 italic px-2">
              "{content.verse}"
            </p>
            <div className="divider-gold !my-4" />
            <p className="text-gold-600 font-montserrat text-sm sm:text-base font-bold">
              {content.verseRef}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 sm:mb-14 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Link to="/our-story" className="btn-secondary w-full sm:w-auto">
              {content.cta1}
            </Link>
            <Link to="/rsvp" className="btn-primary w-full sm:w-auto">
              {content.cta2}
            </Link>
          </div>

          {/* Add to Calendar */}
          <div className={`mb-12 sm:mb-16 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <AddToCalendar />
          </div>

          {/* Scroll Indicator */}
          <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <ArrowDown className="mx-auto text-plum-600 animate-bounce w-8 h-8 sm:w-10 sm:h-10" />
            <p className="text-plum-600 font-montserrat text-xs sm:text-sm mt-2 uppercase tracking-widest font-medium">
              {language === 'fr' ? 'Découvrir' : 'Discover'}
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          WELCOME SECTION - Blanc pur
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="mx-auto text-gold-500 fill-gold-500 w-12 h-12 sm:w-16 sm:h-16 mb-6 sm:mb-8" />
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-bold text-plum-900 mb-4 sm:mb-6">
              {language === 'fr' ? 'Bienvenue' : 'Welcome'}
            </h2>
            
            <div className="divider-gold" />
            
            <p className="text-base sm:text-lg lg:text-xl text-plum-700 font-montserrat leading-relaxed px-4">
              {language === 'fr'
                ? "Nous sommes ravis de vous accueillir sur notre site de mariage. Rejoignez-nous le 14 août 2026 pour célébrer notre union et partager ce moment unique avec nous."
                : "We are delighted to welcome you to our wedding website. Join us on August 14, 2026 to celebrate our union and share this unique moment with us."}
            </p>
          </div>
        </div>
      </section>

      {/* Key Moments */}
      <KeyMoments />

      {/* ═══════════════════════════════════════════════════════════════
          INFO CARDS SECTION - Fond blanc
          ═══════════════════════════════════════════════════════════════ */}
      <section className="py-16 sm:py-24 lg:py-32 bg-gradient-to-b from-white via-lilac-50/30 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 max-w-4xl mx-auto">

            {/* Card 1 - Story */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-medium border border-lilac-100 hover:shadow-hover hover:-translate-y-2 transition-all duration-500 group text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 bg-lilac-50 rounded-full flex items-center justify-center group-hover:bg-gold-50 transition-colors duration-500">
                <Heart className="text-lilac-500 group-hover:text-gold-600 fill-lilac-500 group-hover:fill-gold-600 transition-all w-8 h-8 sm:w-10 sm:h-10" />
              </div>
              <h3 className="text-xl sm:text-2xl font-playfair font-bold text-plum-900 mb-4 group-hover:text-gold-600 transition-colors">
                {language === 'fr' ? 'Notre Histoire' : 'Our Story'}
              </h3>
              <p className="text-plum-700 font-montserrat mb-6 text-sm sm:text-base">
                {language === 'fr'
                  ? 'Découvrez comment notre amour a grandi'
                  : 'Discover how our love grew'}
              </p>
              <Link to="/our-story" className="inline-flex items-center text-gold-600 font-montserrat font-semibold hover:text-gold-700 transition-colors text-sm sm:text-base group-hover:underline underline-offset-4">
                {language === 'fr' ? 'Lire notre histoire' : 'Read our story'}
                <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>

            {/* Card 3 - RSVP */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-medium border border-lilac-100 hover:shadow-hover hover:-translate-y-2 transition-all duration-500 group text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 bg-lilac-50 rounded-full flex items-center justify-center group-hover:bg-gold-50 transition-colors duration-500">
                <Users className="text-lilac-500 group-hover:text-gold-600 transition-colors w-8 h-8 sm:w-10 sm:h-10" />
              </div>
              <h3 className="text-xl sm:text-2xl font-playfair font-bold text-plum-900 mb-4 group-hover:text-gold-600 transition-colors">
                {language === 'fr' ? 'Je m\'enregistre' : 'Register'}
              </h3>
              <p className="text-plum-700 font-montserrat mb-6 text-sm sm:text-base">
                {language === 'fr'
                  ? 'Confirmez votre présence'
                  : 'Confirm your attendance'}
              </p>
              <Link to="/rsvp" className="btn-primary btn-sm sm:btn-primary">
                {language === 'fr' ? 'Répondre' : 'Respond'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
